/**
 * ASC 606 Phase 10e — SSP Library Viewer
 * ======================================
 *
 * Browser for the append-only SSP library (Phase 10a). Three tabs:
 *   • Observations — all OBSERVED/MANUAL rows, sortable, filterable by
 *     entity + source. Each row shows the description, entity, currency,
 *     observed price, source, confidence, and observation date.
 *   • Lookup — reviewer enters a free-text description; UI calls the
 *     lookup endpoint (with entity cascade) and displays the most-recent
 *     observation that matches the normalized item_key.
 *   • Seed — admin surface to record a MANUAL observation (per-entity
 *     price list). POST /ssp-library with source=MANUAL.
 *
 * Backend:
 *   GET /api/v1/revenue/ssp-library
 *   GET /api/v1/revenue/ssp-library/lookup?description=...
 *   POST /api/v1/revenue/ssp-library
 */

import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';

const API = `${API_V1}/revenue`;

interface SSPObservation {
  id: number;
  entity_code: string | null;
  item_key: string;
  item_description: string;
  currency: string;
  observed_price: number;
  source_contract_id: number | null;
  source_pob_id: number | null;
  source: string;           // OBSERVED | MANUAL | RESIDUAL_MARKER
  confidence: string;       // HIGH | MEDIUM | LOW | RESIDUAL
  observation_date: string;
  observed_by: number | null;
  notes: string | null;
}

interface LookupResult {
  description: string;
  item_key: string;
  currency: string;
  entity_code: string | null;
  observation: SSPObservation | null;
}

export default function SSPLibrary() {
  const [tab, setTab] = useState<'obs' | 'lookup' | 'seed'>('obs');
  const [observations, setObservations] = useState<SSPObservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [entityFilter, setEntityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Lookup tab state
  const [lookupDesc, setLookupDesc] = useState('');
  const [lookupEntity, setLookupEntity] = useState('');
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  // Seed tab state
  const [seedDesc, setSeedDesc] = useState('');
  const [seedPrice, setSeedPrice] = useState('');
  const [seedEntity, setSeedEntity] = useState('');
  const [seedCurrency, setSeedCurrency] = useState('USD');
  const [seedNotes, setSeedNotes] = useState('');
  const [seedBusy, setSeedBusy] = useState(false);

  const loadObservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (entityFilter) params.set('entity_code', entityFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      params.set('limit', '500');
      const res = await authFetch(`${API}/ssp-library?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setObservations(data.observations || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [entityFilter, sourceFilter]);

  useEffect(() => {
    if (tab === 'obs') loadObservations();
  }, [tab, loadObservations]);

  const handleLookup = async () => {
    if (!lookupDesc.trim()) return;
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const params = new URLSearchParams();
      params.set('description', lookupDesc);
      if (lookupEntity) params.set('entity_code', lookupEntity);
      const res = await authFetch(`${API}/ssp-library/lookup?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLookupResult(await res.json());
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLookupBusy(false);
    }
  };

  const handleSeed = async () => {
    if (!seedDesc.trim() || !seedPrice.trim()) return;
    setSeedBusy(true);
    try {
      const body: any = {
        description: seedDesc,
        price: Number(seedPrice),
        currency: seedCurrency || 'USD',
        source: 'MANUAL',
      };
      if (seedEntity) body.entity_code = seedEntity;
      if (seedNotes) body.notes = seedNotes;
      const res = await authFetch(`${API}/ssp-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `HTTP ${res.status}`);
      }
      setFlash(`Manual observation recorded · ${seedDesc}`);
      setSeedDesc(''); setSeedPrice(''); setSeedEntity(''); setSeedNotes('');
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setSeedBusy(false);
    }
  };

  const kpis = {
    total: observations.length,
    entities: new Set(observations.map((o) => o.entity_code || '(org-wide)')).size,
    item_keys: new Set(observations.map((o) => o.item_key)).size,
    observed: observations.filter((o) => o.source === 'OBSERVED').length,
    manual: observations.filter((o) => o.source === 'MANUAL').length,
  };

  const columns: Column[] = [
    {
      key: 'item_description', label: 'Description', width: 260,
      render: (_v: any, r: SSPObservation) => (
        <div>
          <div style={{ color: 'var(--text-primary)', fontSize: 12 }}>
            {r.item_description.length > 70 ? r.item_description.slice(0, 70) + '…' : r.item_description}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2, fontFamily: 'monospace' }}>
            {r.item_key}
          </div>
        </div>
      ),
    },
    { key: 'entity_code', label: 'Entity', width: 80, render: (v: string | null) => v || '—' },
    { key: 'currency', label: 'Ccy', width: 60 },
    {
      key: 'observed_price', label: 'Price', align: 'right', width: 110,
      render: (v: number) => <span style={{ fontWeight: 500 }}>{formatAmount(v)}</span>,
    },
    {
      key: 'source', label: 'Source', width: 100,
      render: (v: string) => {
        const colors: Record<string, string> = {
          OBSERVED: 'var(--color-accent)',
          MANUAL: 'var(--color-positive)',
          RESIDUAL_MARKER: 'var(--color-warning)',
        };
        return (
          <span style={{
            padding: '2px 6px', borderRadius: 10, fontSize: 10,
            color: colors[v] || 'var(--text-secondary)',
            border: `1px solid ${colors[v] || 'var(--border-subtle)'}`,
          }}>{v}</span>
        );
      },
    },
    { key: 'confidence', label: 'Confidence', width: 90 },
    {
      key: 'observation_date', label: 'Observed', width: 120,
      render: (v: string | null) => v ? new Date(v).toISOString().slice(0, 10) : '—',
    },
    {
      key: 'source_contract_id', label: 'From Contract', width: 110,
      render: (v: number | null) => v ? `#${v}` : '—',
    },
  ];

  return (
    <PageLayout
      title="SSP Library"
      subtitle="ASC 606 Step 4 — append-only standalone selling price observations. Seeds from approved contracts (OBSERVED) or admin entry (MANUAL)."
    >
      <TabBar
        tabs={[
          { key: 'obs', label: 'Observations' },
          { key: 'lookup', label: 'Lookup' },
          { key: 'seed', label: 'Seed MANUAL' },
        ]}
        active={tab}
        onChange={(k) => { setTab(k as any); setError(null); }}
      />

      {flash && (
        <div style={{ padding: 8, marginTop: 12, background: 'rgba(34,197,94,0.10)', color: 'var(--color-positive)', borderRadius: 4 }}>
          {flash}
        </div>
      )}
      {error && (
        <div style={{ padding: 8, marginTop: 12, background: 'rgba(239,68,68,0.10)', color: 'var(--color-negative)', borderRadius: 4 }}>
          {error}
        </div>
      )}

      {tab === 'obs' && (
        <>
          <div style={{ marginTop: 16 }}>
            <KpiGrid items={[
              { label: 'Observations', value: String(kpis.total) },
              { label: 'Distinct Items', value: String(kpis.item_keys) },
              { label: 'Entities', value: String(kpis.entities) },
              { label: 'OBSERVED', value: String(kpis.observed) },
              { label: 'MANUAL', value: String(kpis.manual) },
            ]} />
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Entity:
              <input
                type="text" value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
                placeholder="e.g. 3000"
                style={{ marginLeft: 6, padding: 4, fontSize: 12, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Source:
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                style={{ marginLeft: 6, padding: 4, fontSize: 12, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              >
                <option value="">All</option>
                <option value="OBSERVED">OBSERVED</option>
                <option value="MANUAL">MANUAL</option>
                <option value="RESIDUAL_MARKER">RESIDUAL_MARKER</option>
              </select>
            </label>
            <button
              onClick={loadObservations}
              style={{ padding: '4px 10px', fontSize: 12, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)', cursor: 'pointer' }}
            >Apply</button>
          </div>
          <div style={{ marginTop: 12 }}>
            <DataTable
              rows={observations}
              columns={columns}
              loading={loading}
              emptyMessage="No observations match the current filters."
              rowKey="id"
            />
          </div>
        </>
      )}

      {tab === 'lookup' && (
        <div style={{ marginTop: 16, maxWidth: 700 }}>
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            Enter a description to find the most-recent observation (entity-specific first, then org-wide).
            The server normalizes (lowercase / whitespace-collapsed / punctuation-stripped) before hashing to the item_key.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="text" value={lookupDesc}
              onChange={(e) => setLookupDesc(e.target.value)}
              placeholder="e.g. Software License — Analytics Platform"
              style={{ padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
            <input
              type="text" value={lookupEntity}
              onChange={(e) => setLookupEntity(e.target.value)}
              placeholder="Entity code (optional — falls back to org-wide)"
              style={{ padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
            <button
              onClick={handleLookup}
              disabled={lookupBusy || !lookupDesc.trim()}
              style={{
                padding: '8px 14px', fontSize: 13, background: 'var(--color-accent)',
                color: '#fff', border: 'none', borderRadius: 4,
                cursor: lookupBusy || !lookupDesc.trim() ? 'not-allowed' : 'pointer',
                opacity: lookupBusy || !lookupDesc.trim() ? 0.5 : 1,
                width: 'fit-content',
              }}
            >{lookupBusy ? 'Looking up…' : 'Lookup'}</button>
          </div>

          {lookupResult && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                item_key · <span style={{ fontFamily: 'monospace' }}>{lookupResult.item_key}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-primary)' }}>
                {lookupResult.observation ? (
                  <>
                    <strong>{formatAmount(lookupResult.observation.observed_price)}</strong> {lookupResult.observation.currency}
                    {' · '}
                    <span>{lookupResult.observation.source} / {lookupResult.observation.confidence}</span>
                    {' · '}
                    <span>observed {new Date(lookupResult.observation.observation_date).toISOString().slice(0, 10)}</span>
                    {lookupResult.observation.entity_code && <> · entity {lookupResult.observation.entity_code}</>}
                  </>
                ) : (
                  <span style={{ color: 'var(--color-warning)' }}>No observation found for this item_key + entity.</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'seed' && (
        <div style={{ marginTop: 16, maxWidth: 700 }}>
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            Record a MANUAL observation — the admin-curated price list. OBSERVED entries flow automatically from approved contracts; use this only to seed the library from price catalogs or market data.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="text" value={seedDesc}
              onChange={(e) => setSeedDesc(e.target.value)}
              placeholder="Description (required)"
              style={{ padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
            <input
              type="number" value={seedPrice} step="0.01"
              onChange={(e) => setSeedPrice(e.target.value)}
              placeholder="Price (required)"
              style={{ padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                type="text" value={seedEntity}
                onChange={(e) => setSeedEntity(e.target.value)}
                placeholder="Entity code (optional — org-wide if empty)"
                style={{ flex: 1, padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
              <input
                type="text" value={seedCurrency}
                onChange={(e) => setSeedCurrency(e.target.value.toUpperCase())}
                placeholder="USD"
                maxLength={5}
                style={{ width: 80, padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </div>
            <textarea
              value={seedNotes}
              onChange={(e) => setSeedNotes(e.target.value)}
              placeholder="Notes — evidence source (catalog page, market study, etc.)"
              rows={3}
              style={{ padding: 8, fontSize: 13, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <button
              onClick={handleSeed}
              disabled={seedBusy || !seedDesc.trim() || !seedPrice.trim()}
              style={{
                padding: '8px 14px', fontSize: 13, background: 'var(--color-positive)',
                color: '#fff', border: 'none', borderRadius: 4,
                cursor: seedBusy || !seedDesc.trim() || !seedPrice.trim() ? 'not-allowed' : 'pointer',
                opacity: seedBusy || !seedDesc.trim() || !seedPrice.trim() ? 0.5 : 1,
                width: 'fit-content',
              }}
            >{seedBusy ? 'Recording…' : 'Record MANUAL Observation'}</button>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
